import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  CustomResultOfPaginatedListOfNumberInformationWithPagination,
  NumberInformationWithPagination,
  PaginatedListOfNumberInformationWithPagination,
} from 'src/app/core/api/models';
import { AppDomService } from 'src/app/core/services/dom.service';
import { ComparisonOperator } from '../../../core/api/models/comparison-operator';
import { BulkServicesService } from '../../../core/api/services';
import { EventTypes } from '../../../core/constants/event-types';
import { DialogStatus } from '../../../core/services/dialog.service';
import { BaseIndexPageComponent } from '../../shared/base/components/base-index-page.component';
import { GetEnumValuesDto } from './../../../core/api/models/get-enum-values-dto';
import { NumberDto } from './../../../core/api/models/number-dto';
import { FormPageConfig } from './../../../core/models/form-page-config.model';
import { PageNames } from 'src/app/configs/page-names.enum';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';
import { BulkNumberInformationFormComponent } from "./bulk-number-information-form/bulk-number-information-form.component";
import { BulkNumbersDeleteFromXlsxComponent } from "./bulk-number-delete-from-xlsx/bulk-numbers-delete-from-xlsx.component";
import { SimTypeDirective } from './shared/sim-type.directive';


@Component({
    selector: 'app-bulk-number-information',
    templateUrl: './bulk-number-information.component.html',
    styleUrls: ['./bulk-number-information.component.scss'],
    imports: [BaseIndexModule, BulkNumberInformationFormComponent, BulkNumbersDeleteFromXlsxComponent, SimTypeDirective]
})
export class BulkNumberInformationComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties

  pageName = PageNames.bulkNumberInformation;
  protected currentService = inject(BulkServicesService);
  modelWithPagination$: Observable<PaginatedListOfNumberInformationWithPagination>;
  items$: Observable<Array<NumberInformationWithPagination>>;
  formPageConfig: FormPageConfig<NumberDto> = {};

  isShowNumbersImportFromXlsx: boolean = false;
  isShowNumbersDeleteFromXlsx: boolean = false;
  isTooltipDisabled: boolean = true;

  SimTypeEnumValues: GetEnumValuesDto[] = [];
  SmsAggregatorEnumValues: GetEnumValuesDto[] = [];
  GenderTypeEnumValues: GetEnumValuesDto[] = [];

  //#endregion

  constructor(
    public cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.pageConfig.showAddBtn = false;
      this.pageConfig.showAuditBtn = false;
      this.pageConfig.tableConfig.showOperationColumn = false;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  getEnumsValues() {
    this.getEnumValuesApi('SimType')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.SimTypeEnumValues = values));
    this.getEnumValuesApi('SmsAggregator')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.SmsAggregatorEnumValues = values));
    this.getEnumValuesApi('GenderType')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.GenderTypeEnumValues = values));
  }

  configTableColumns() {
    this.tableColumns = [
      {
        title: `${this.tableVocabs}mobile12`,
        name: 'mobile',
        hasSearch: true,
        noSorting: true,
        customOperators: [
          ComparisonOperator.EqualTo,
          ComparisonOperator.StartsWith,
        ],
        wrap: true,
        dir: 'ltr',
      },
      {
        title: `${this.tableVocabs}stateName`,
        name: 'stateId',
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}cityName`,
        name: 'cityId',
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}genderType`,
        name: 'genderType',
        hasSearch: true,
        type: 'enum',
        enumName: 'GenderType',
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}guildName`,
        name: 'guildId',
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}subGuildName`,
        name: 'cityId',
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}mobileOperator`,
        name: 'mobileOperator',
        hasSearch: true,
        type: 'enum',
        enumName: 'SmsAggregator',
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}simType`,
        name: 'simType',
        hasSearch: true,
        type: 'enum',
        enumName: 'SimType',
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}zipCode10`,
        name: 'zipCode10',
        hasSearch: true,
        noSorting: true,
        customOperators: [
          ComparisonOperator.EqualTo,
          ComparisonOperator.StartsWith,
        ],
        wrap: true,
      },
    ];
  }

  //#region Api

  //Get All
  getPaginationApi(): Observable<CustomResultOfPaginatedListOfNumberInformationWithPagination> {
    return this.currentService.bulkServicesGetBulkNumberInformationWithPagination(
      { body: { ...this.getBaseModelWithPaginationParams() } }
    );
  }

  //#endregion

  //#region Group selection

  onGroupDeletion() {
    if (this.selectedIds?.length === 0)
      this.dialogService.openDialog({
        title: 'common.error',
        message: 'messages.selectItems',
        status: DialogStatus.error,
      });

    super.onGroupDeletion();
  }

  //#endregion

  //#region Import Numbers from xlsx

  getImportNumbersFromXlsxResponse() {
    this.getTableData();
    this.isShowNumbersImportFromXlsx = !this.isShowNumbersImportFromXlsx;
  }
  getDeleteNumbersFromXlsxResponse() {
    this.getTableData();
    this.isShowNumbersDeleteFromXlsx = !this.isShowNumbersDeleteFromXlsx;
  }

  onShowDeleteNumbersFromXlsx() {
    this.pageConfig.loading = true;
    this.isShowNumbersDeleteFromXlsx = true;
  }
  tooltipDisabled(mobileOperator: number) {
    if (mobileOperator == 1 || mobileOperator == 2 || mobileOperator == 3)
      this.isTooltipDisabled = false;
    else this.isTooltipDisabled = true;

    return this.isTooltipDisabled;
  }

  async getAllCheckBoxesAsync(): Promise<NodeListOf<Element>> {
    try {
      return AppDomService.getElementsByQueryAsync(
        `${this.tagName} [data-checkbox-id]`,
        '',
        5,
        6000
      );
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }
}
